import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useApprovePlaceClaimMutation,
  useGetPlaceClaimQuery,
  useGetPlaceClaimsQuery,
  useRejectPlaceClaimMutation,
} from '../redux/adminApiSlice';

const STATUS_OPTIONS = [
  'paid_pending_review,pending_payment',
  'paid_pending_review',
  'pending_payment',
  'approved',
  'rejected',
  'draft',
] as const;

const STATUS_LABELS: Record<string, string> = {
  'paid_pending_review,pending_payment': 'Awaiting review (paid + email-only)',
};

/** Both statuses are actionable: paid (Stripe) and email-only (no Stripe key). */
function isActionableStatus(status?: string): boolean {
  return status === 'paid_pending_review' || status === 'pending_payment';
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function placeName(place: unknown): string {
  if (place && typeof place === 'object' && 'name' in place) {
    return String((place as { name?: string }).name || 'Place');
  }
  return String(place || '—');
}

function userLabel(user: unknown): string {
  if (user && typeof user === 'object') {
    const u = user as { username?: string; email?: string; _id?: string };
    return u.username || u.email || u._id || '—';
  }
  return String(user || '—');
}

const CLAIM_KIND_LABELS: Record<string, string> = {
  existing_google: 'Existing (Google)',
  switch_owner_user_created: 'Switch owner (user-created)',
  imported_hidden: 'Imported (hidden)',
};

function claimKindLabel(kind?: string): string {
  if (!kind) return 'Existing (Google)';
  return CLAIM_KIND_LABELS[kind] || kind;
}

/** On/off-grid label: imported_hidden claims originated off-grid. */
function isOffGridClaim(kind?: string): boolean {
  return kind === 'imported_hidden';
}

function joinedUserCount(place: unknown): number {
  if (place && typeof place === 'object' && 'userCount' in place) {
    const c = (place as { userCount?: number }).userCount;
    return typeof c === 'number' ? c : 0;
  }
  return 0;
}

const PlaceClaimsPage: React.FC = () => {
  const [status, setStatus] = useState<string>('paid_pending_review,pending_payment');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  const listQuery = useGetPlaceClaimsQuery({ status, limit: 100 });
  const detailQuery = useGetPlaceClaimQuery(selectedId || '', { skip: !selectedId });
  const [approve, approveState] = useApprovePlaceClaimMutation();
  const [reject, rejectState] = useRejectPlaceClaimMutation();

  const items = listQuery.data?.items || [];

  const selected = useMemo(() => detailQuery.data?.claim, [detailQuery.data]);

  const onApprove = async () => {
    if (!selectedId) return;
    setActionMessage(null);
    try {
      await approve({ id: selectedId }).unwrap();
      setActionMessage('Claim approved — claimant added as owner.');
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setActionMessage(e?.data?.message || e?.message || 'Approve failed');
    }
  };

  const onReject = async () => {
    if (!selectedId) return;
    setActionMessage(null);
    try {
      await reject({ id: selectedId, reason: rejectReason || undefined }).unwrap();
      setRejectOpen(false);
      setRejectReason('');
      setActionMessage('Claim rejected.');
    } catch (err: unknown) {
      const e = err as { data?: { message?: string }; message?: string };
      setActionMessage(e?.data?.message || e?.message || 'Reject failed');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Place claims
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paid claims enter this queue. Approve adds the claimant as a place owner.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {STATUS_LABELS[s] || s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button onClick={() => listQuery.refetch()} disabled={listQuery.isFetching}>
          Refresh
        </Button>
        {listQuery.isFetching ? <CircularProgress size={20} /> : null}
      </Stack>

      {actionMessage ? (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setActionMessage(null)}>
          {actionMessage}
        </Alert>
      ) : null}

      {listQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load claims.
        </Alert>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Place</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Origin</TableCell>
            <TableCell>Joined</TableCell>
            <TableCell>Claimant</TableCell>
            <TableCell>Work email</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Paid at</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((row: any) => (
            <TableRow
              key={row._id}
              hover
              selected={selectedId === row._id}
              onClick={() => setSelectedId(row._id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{placeName(row.placeId)}</TableCell>
              <TableCell>
                <Chip size="small" variant="outlined" label={claimKindLabel(row.claimKind)} />
              </TableCell>
              <TableCell>
                {isOffGridClaim(row.claimKind) ? (
                  <Chip size="small" color="warning" label="Off Grid" />
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="On Grid" />
                )}
              </TableCell>
              <TableCell>{joinedUserCount(row.placeId)}</TableCell>
              <TableCell>{userLabel(row.userId)}</TableCell>
              <TableCell>
                {row.businessEmail}{' '}
                {row.businessEmailVerifiedAt ? (
                  <Chip size="small" color="success" label="verified" />
                ) : (
                  <Chip size="small" label="unverified" variant="outlined" />
                )}
              </TableCell>
              <TableCell>{row.phone}</TableCell>
              <TableCell>{formatDate(row.paidAt)}</TableCell>
              <TableCell>{row.status}</TableCell>
            </TableRow>
          ))}
          {!items.length && !listQuery.isLoading ? (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography color="text.secondary">No claims for this filter.</Typography>
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <Dialog
        open={Boolean(selectedId)}
        onClose={() => setSelectedId(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Claim detail</DialogTitle>
        <DialogContent dividers>
          {detailQuery.isLoading ? (
            <CircularProgress size={24} />
          ) : selected ? (
            <Stack spacing={1.5}>
              {selected.claimKind === 'switch_owner_user_created' ? (
                <Alert severity="warning">
                  Approving <strong>transfers ownership</strong> of the user-created place
                  {' '}"{placeName(selected.placeId)}" to the claimant, and enriches it from
                  Google. The prior creator/owner will be replaced.
                </Alert>
              ) : null}
              {selected.claimKind === 'imported_hidden' ? (
                <Alert severity="info">
                  This place was imported <strong>hidden</strong> from Google for this claim.
                  Approving reveals it publicly and makes the claimant the sole owner.
                </Alert>
              ) : null}
              <Typography>
                <strong>Status:</strong> {selected.status}
              </Typography>
              <Typography component="div">
                <strong>Kind:</strong>{' '}
                <Chip size="small" variant="outlined" label={claimKindLabel(selected.claimKind)} />
                {' '}
                {isOffGridClaim(selected.claimKind) ? (
                  <Chip size="small" color="warning" label="Off Grid" />
                ) : (
                  <Chip size="small" color="success" variant="outlined" label="On Grid" />
                )}
              </Typography>
              <Typography>
                <strong>Joined users:</strong> {joinedUserCount(selected.placeId)}
              </Typography>
              <Typography>
                <strong>Place:</strong> {placeName(selected.placeId)}{' '}
                {selected.placeId &&
                typeof selected.placeId === 'object' &&
                '_id' in selected.placeId ? (
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({String((selected.placeId as { _id: string })._id)})
                  </Typography>
                ) : null}
              </Typography>
              {selected.placeId &&
              typeof selected.placeId === 'object' &&
              'googlePlaceId' in selected.placeId ? (
                <Typography variant="body2">
                  Google place id: {String((selected.placeId as any).googlePlaceId || '—')}
                </Typography>
              ) : null}
              <Typography>
                <strong>Claimant:</strong> {userLabel(selected.userId)}
              </Typography>
              <Typography>
                <strong>Contact:</strong> {selected.contactName} ({selected.role})
              </Typography>
              <Typography component="div">
                <strong>Work email:</strong> {selected.businessEmail}{' '}
                {selected.businessEmailVerifiedAt ? (
                  <Chip size="small" color="success" label="verified" sx={{ ml: 1 }} />
                ) : (
                  <Chip size="small" label="unverified" variant="outlined" sx={{ ml: 1 }} />
                )}
                {selected.emailMatchesWebsiteDomain ? (
                  <Chip size="small" color="success" label="domain match" sx={{ ml: 1 }} />
                ) : null}
              </Typography>
              <Typography>
                <strong>Phone:</strong> {selected.phone}
              </Typography>
              {selected.pendingPlaceName ||
              selected.pendingPlaceCity ||
              selected.pendingPlaceState ||
              selected.pendingPlaceAddress ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>Typed at search:</strong>{' '}
                  {[
                    selected.pendingPlaceName,
                    selected.pendingPlaceAddress,
                    selected.pendingPlaceCity,
                    selected.pendingPlaceState,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </Typography>
              ) : null}
              {selected.note ? (
                <Typography>
                  <strong>Note:</strong> {selected.note}
                </Typography>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                Stripe session: {selected.stripeCheckoutSessionId || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paid: {formatDate(selected.paidAt)} · Created: {formatDate(selected.createdAt)}
              </Typography>
              {selected.adminNote ? (
                <Typography>
                  <strong>Admin note:</strong> {selected.adminNote}
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography>Claim not found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedId(null)}>Close</Button>
          {isActionableStatus(selected?.status) ? (
            <>
              <Button
                color="error"
                onClick={() => setRejectOpen(true)}
                disabled={rejectState.isLoading || approveState.isLoading}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                onClick={() => void onApprove()}
                disabled={approveState.isLoading || rejectState.isLoading}
              >
                Approve
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject claim</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void onReject()}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlaceClaimsPage;
